import { useMemo } from 'react';

import { useGetApiV1TutoraiDashboardReport } from '@/shared/api/generated';

import { normalizeDashboardReport } from '../utils/normalizeTutorAi';
import { isUsagePeriodValid } from '../utils/usagePeriod';
import { buildUsageTimeline } from '../utils/usageTimeline';
import { useTutorUsageFilters } from './useTutorUsageFilters';

/**
 * Consolidado agregado de uso do tutor no período filtrado.
 *
 * O filtro de turma vem do átomo como string (é o que o componente de seleção usa) e é
 * convertido para número na borda, porque a API espera `int64`. Filtro vazio é omitido do
 * request em vez de ir como `0` — `0` seria interpretado como um id.
 *
 * A API não tem mais recorte por curso: o backend cria uma turma por curso, então o filtro
 * de turma já é o mesmo recorte.
 *
 * A query só dispara com um período válido — ver `isUsagePeriodValid`, que é a fonte única
 * da regra e documenta cada gate.
 *
 * `timeline` é construída a partir de `filters.dateStart`/`filters.dateEnd`, não de
 * `report.periodStart`/`report.periodEnd` (o período que a API de fato devolveu). Os dois
 * só divergem se o backend clampar a janela pedida — não observado até hoje — e o filtro é
 * a fonte deliberada aqui: é o eixo que a tela promete ao usuário, e o gráfico deve refletir
 * o que foi pedido, não o que o backend decidiu responder. Se isso mudar, o card passaria a
 * desenhar um intervalo maior que os dados que descreve, preenchido de zero na diferença.
 *
 * @returns Indicadores normalizados do período, `timeline` densa para os gráficos (um
 * ponto por dia, zero onde a API omitiu), estados de carregamento/erro, `isPeriodValid`
 * (se o período atual é válido para consulta) e `refetch` para nova tentativa
 *
 * @example
 * ```tsx
 * const { report, isLoading, isPeriodValid, refetch } = useTutorDashboardReport();
 * ```
 */
export function useTutorDashboardReport() {
  const { filters } = useTutorUsageFilters();

  const isPeriodValid = isUsagePeriodValid(filters.dateStart, filters.dateEnd);

  const query = useGetApiV1TutoraiDashboardReport(
    {
      start: filters.dateStart,
      end: filters.dateEnd,
      ...(filters.classStudentId ? { classStudentId: Number(filters.classStudentId) } : {}),
    },
    { query: { enabled: isPeriodValid } },
  );

  // Report e timeline saem do mesmo memo: a timeline é função do report e do período, e
  // separá-los recalcularia a fusão a cada render em que só o outro mudasse.
  const { report, timeline } = useMemo(() => {
    const normalized = normalizeDashboardReport(query.data ?? {});
    return {
      report: normalized,
      timeline: buildUsageTimeline(normalized, filters.dateStart, filters.dateEnd),
    };
  }, [query.data, filters.dateStart, filters.dateEnd]);

  return {
    report,
    timeline,
    isLoading: query.isLoading,
    isError: query.isError,
    isPeriodValid,
    refetch: query.refetch,
  } as const;
}
